import { ApiProperty, PartialType } from './decorators-nest';
import {
  IsDateString,
  IsNumber,
  IsEnum,
  IsOptional,
  IsString,
  IsArray,
  ArrayNotEmpty,
  MaxLength,
} from 'class-validator';

export enum MoodState {
  HAPPY = 'HAPPY',
  SAD = 'SAD',
  SENSITIVE = 'SENSITIVE',
  ANGRY = 'ANGRY',
  PEACEFUL = 'PEACEFUL',
  MOTIVATED = 'MOTIVATED',
  OVERWHELMED = 'OVERWHELMED',
  TIRED = 'TIRED',
}

export enum CervicalMucus {
  DRY = 'seche',
  STICKY = 'collante',
  CREAMY = 'cremeuse',
  EGG_WHITE = 'blanc-oeuf',
}

export enum CervixPosition {
  FIRM = 'ferme',
  MEDIUM = 'medium',
  SOFT = 'souple',
}

export enum OvulationTest {
  NEGATIVE = 'negatif',
  POSITIVE = 'positif',
  LH_PEAK = 'pic-lh',
}

export enum PeriodFlow {
  NONE = 'aucune',
  LIGHT = 'leger',
  MEDIUM = 'moyen',
  HEAVY = 'abondant',
  HEMORRHAGIC = 'hemorragique',
}

export enum Spotting {
  NONE = 'aucun',
  PINK = 'rose',
  BROWN = 'brun',
  BRIGHT_RED = 'rouge-vif',
}

export enum Emotion {
  SERENE = 'sereine',
  HAPPY = 'heureuse',
  IRRITABLE = 'irritable',
  ANXIOUS = 'anxieuse',
  SAD = 'triste',
  SENSITIVE = 'sensible',
  CONFIDENT = 'confiante',
  EXHAUSTED = 'epuisee',
  INDIFFERENT = 'indifferente',
}

export enum Pain {
  NONE = 'aucune',
  LOWER_ABDOMEN = 'bas-ventre',
  BACK = 'dos',
  HEAD = 'tete',
  CHEST = 'poitrine',
  LEGS = 'jambes',
  RIGHT_OVARY = 'ovaire-droit',
  LEFT_OVARY = 'ovaire-gauche',
  OVULATION = 'ovulation',
}

export enum Sexuality {
  INTERCOURSE = 'rapport',
  NO_INTERCOURSE = 'pas-rapport',
  HIGH_LIBIDO = 'libido-haute',
  LOW_LIBIDO = 'libido-basse',
}

export enum Energy {
  VERY_LOW = 'tres-basse',
  LOW = 'basse',
  NORMAL = 'normale',
  HIGH = 'haute',
  TOP = 'au-top',
}

export enum Pms {
  DIARRHEA = 'diarrhee',
  CRAVINGS = 'fringales',
  NAUSEA = 'nausee',
  WATER = 'retensions-d-eau',
  ACNE = 'acne',
  INSOMNIA = 'insomnie',
  BLOATING = 'ballonnements',
  CONSTIPATION = 'constipation',
}

export class CreateHealthMetricsDto {
  @ApiProperty({
    description: 'Patient ID',
    required: true,
    example: 'uuid',
  })
  @IsString()
  public patientId: string;

  @ApiProperty({
    type: 'string',
    format: 'date',
    example: '2025-08-26',
    required: true,
    description: 'Date of the health record',
  })
  @IsDateString()
  public date: string;

  @ApiProperty({ description: 'Calories consumed', example: 2200, required: false })
  @IsNumber()
  @IsOptional()
  public dailyCalories?: number;

  @ApiProperty({ description: 'Hydration amount (liters)', example: 2.5, required: false })
  @IsNumber()
  @IsOptional()
  public hydration?: number;

  @ApiProperty({ description: 'Number of steps', example: 10000, required: false })
  @IsNumber()
  @IsOptional()
  public steps?: number;

  @ApiProperty({ description: 'Calories burned', example: 500, required: false })
  @IsNumber()
  @IsOptional()
  public burnedCalories?: number;

  @ApiProperty({
    description: 'Patient mood state',
    enum: MoodState,
    example: MoodState.HAPPY,
    required: false,
  })
  @IsEnum(MoodState)
  @IsOptional()
  public mood?: MoodState;

  @ApiProperty({ description: 'Émotions (choix multiple)', enum: Emotion, isArray: true, required: false })
  @IsArray()
  @IsEnum(Emotion, { each: true })
  @IsOptional()
  public emotions?: Emotion[];

  @ApiProperty({ description: 'Glaire cervicale', enum: CervicalMucus, required: false })
  @IsEnum(CervicalMucus)
  @IsOptional()
  public cervicalMucus?: CervicalMucus;

  @ApiProperty({ description: "Col de l'utérus", enum: CervixPosition, required: false })
  @IsEnum(CervixPosition)
  @IsOptional()
  public cervixPosition?: CervixPosition;

  @ApiProperty({ description: "Test d'ovulation", enum: OvulationTest, required: false })
  @IsEnum(OvulationTest)
  @IsOptional()
  public ovulationTest?: OvulationTest;

  @ApiProperty({ description: 'Règles', enum: PeriodFlow, required: false })
  @IsEnum(PeriodFlow)
  @IsOptional()
  public periodFlow?: PeriodFlow;

  @ApiProperty({ description: 'Spotting', enum: Spotting, required: false })
  @IsEnum(Spotting)
  @IsOptional()
  public spotting?: Spotting;

  @ApiProperty({ description: 'Douleurs (choix multiple)', enum: Pain, isArray: true, required: false })
  @IsArray()
  @IsEnum(Pain, { each: true })
  @IsOptional()
  public pains?: Pain[];

  @ApiProperty({ description: 'Sexualité (choix multiple)', enum: Sexuality, isArray: true, required: false })
  @IsArray()
  @IsEnum(Sexuality, { each: true })
  @IsOptional()
  public sexuality?: Sexuality[];

  @ApiProperty({ description: 'Énergie', enum: Energy, required: false })
  @IsEnum(Energy)
  @IsOptional()
  public energy?: Energy;

  @ApiProperty({ description: 'SPM & autres (choix multiple)', enum: Pms, isArray: true, required: false })
  @IsArray()
  @IsEnum(Pms, { each: true })
  @IsOptional()
  public pms?: Pms[];

  @ApiProperty({ description: 'Note du jour (max 500 caractères)', required: false })
  @IsString()
  @MaxLength(500)
  @IsOptional()
  public dailyNote?: string;

  @ApiProperty({ description: 'Tags personnalisés', type: [String], required: false })
  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  public customTags?: string[];
}

export class UpdateHealthMetricsDto extends PartialType(CreateHealthMetricsDto) {}

export class AddMealsCaloriesDto {
  @ApiProperty({ description: 'ID du patient' })
  @IsString()
  patientId: string;

  @ApiProperty({ description: 'Date de la mesure' })
  @IsDateString()
  date: string;

  @ApiProperty({ description: 'Liste des IDs des repas', type: [Number] })
  @IsArray()
  @ArrayNotEmpty()
  @IsNumber({}, { each: true })
  ids: number[];
}
